import React, { useState, useEffect, useRef, useMemo } from 'react';
import { CircularProgress, Box } from '@mui/material';
import FormDialog from './dialog';
import { MdEditNote, MdDelete } from "react-icons/md";
import { Tooltip } from 'react-tooltip';
import PackageDialog from '../course/packagedialog';
import axios from 'axios';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import DataTable from '../ui/DataTable';
import { StatusBadge, Chip } from '../ui/primitives';
import EnrollmentRowActionsMenu from './EnrollmentRowActionsMenu';
import PageActionsMenu, {
  ActionsMenuItem,
  ActionsSection,
} from '../ui/PageActionsMenu';
import { Plus, Mail, Upload, BarChart2 } from 'lucide-react';
import { getCache, setCache, clearCacheByPrefix } from '../ui/listCache';
import DeleteConfirmDialog from '../ui/DeleteConfirmDialog';
import { useConfirm } from '../ui/ConfirmProvider';
import { useCourseFilter } from '../context/CourseFilterContext';
import { LabelWithHelp } from '../ui/FieldHelp';
import { ENROLLMENT_LIST_FIELD_HELP as HELP } from '../ui/listScreenFieldHelp';
import { useAppContext } from '../../appprovider';
import { readResponseOrGate } from '../services/apiGate';
import {
  INSTRUCTOR_METERS,
  isMeterAtCap,
  meterGateBody,
} from '../services/instructorMeterLimits';
import InstructorListStatsRow from '../ui/InstructorListStatsRow';
import {
  buildEnrollmentListQuery,
  buildEnrollmentListCacheKey,
  parseEnrollmentListResponse,
  ENROLLMENT_LIST_DEFAULTS,
  ENROLLMENT_SORT_FIELDS,
  getNextEnrollmentSort,
  sortEnrollmentsLocally,
} from './enrollmentListApi';
import useDemoActionGate from '../hooks/useDemoActionGate';
import DemoLockedDialog from '../ui/DemoLockedDialog';

const ENROLLMENT_PAGE_SIZE = 10;

const PageShell = styled.div`

  min-height: calc(100vh - 32px);
  margin: 0 auto;
  padding: 0 28px 36px;
  color: ${({ theme }) => theme.text};
  font-family: ${({ theme }) => theme.fontBody};

  select {
    height: ${({ theme }) => theme.controlH};
    padding: 0 34px 0 12px;
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: ${({ theme }) => theme.rMd};
    background: ${({ theme }) => theme.surface};
    color: ${({ theme }) => theme.text};
    font-size: ${({ theme }) => theme.fsSm};
    font-weight: 650;
    outline: none;
    box-shadow: 0 1px 2px rgba(17, 20, 38, 0.06);
  }

  @media (max-width: 768px) {
    padding: 0 14px 28px;
  }
`;

const PageHero = styled.div`
  width: min(1180px, 100%);
  margin: ${({ theme }) => theme.space} auto ${({ theme }) => theme.gap};
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;

  h2 {
    margin: 0;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.fontHead};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -.01em;
  }

  p {
    margin: 2px 0 0;
    color: ${({ theme }) => theme.textMuted};
    font-size: ${({ theme }) => theme.fsSm};
  }
`;

const TableWrap = styled.div`
  width: min(1180px, 100%);
  margin: 0 auto;
`;

const initialValue = { courseId: "", studentId: "", courseName: "", firstName: "", lastName: "", studentEmail: "" };

const Grids = () => {
  const askConfirmation = useConfirm();
  const { openUpgradeGate, entitlements, entitlementsLoading, loadEntitlements } = useAppContext();
  const { isLocked, guardAction, lockProps, lockedDialogOpen, setLockedDialogOpen } = useDemoActionGate();
  const apiUrl = process.env.REACT_APP_API_URL;
  const [gridApi, setGridApi] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [tableData, setTableData] = useState(null);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState(initialValue);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(localStorage.getItem('userId'));
  const [isPackageDialogOpen, setPackageDialogOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [enrollmentPage, setEnrollmentPage] = useState(0);
  const [enrollmentTotalPages, setEnrollmentTotalPages] = useState(1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [enrollmentSortBy, setEnrollmentSortBy] = useState(ENROLLMENT_LIST_DEFAULTS.sortBy);
  const [enrollmentSortDir, setEnrollmentSortDir] = useState(ENROLLMENT_LIST_DEFAULTS.sortDir);
  const searchDebounceRef = useRef(null);
  const navigate = useNavigate();
  const userType = localStorage.getItem("userType");
  const [columnDefs, setColumnDefs] = useState([]);
     const [isMobile,setIsMobile] = useState(false)
     const [studentId, setStudentId] = useState();
  // Course filter now lives in the global TopBar dropdown via shared context.
  // selectedCourseId === '' means "All courses" (server returns every course).
  const { selectedCourseId } = useCourseFilter();
  useEffect(() => {
    const planExpire = localStorage.getItem('planExpire');

    if (planExpire === "1" && userType!=2) {
      sessionStorage.removeItem('planRenewDismissed');
      navigate('/Portal/Instructor');
    }
  }, [navigate]);

  const getBasicAuthHeader = () => {
    const username = process.env.REACT_APP_API_USERNAME;
    const password = process.env.REACT_APP_API_PASSWORD;
    const encodedCredentials = btoa(`${username}:${password}`);
    return `Basic ${encodedCredentials}`;
  };

  /** Drop all cached enrollment-list pages for this user after a mutation. */
  const invalidateEnrollmentListCache = () => {
    if (userId) clearCacheByPrefix(`enrollments:${userId}:`);
  };

  const applyEnrollmentListPage = (parsed) => {
    setTableData(parsed.content);
    setRowCount(parsed.totalElements);
    setEnrollmentTotalPages(Math.max(parsed.totalPages, 1));
    setEnrollmentPage(parsed.page);
  };

  useEffect(() => {
    const userIdFromStorage = localStorage.getItem('userId');
    setUserId(userIdFromStorage);
  }, []);

  useEffect(() => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedKeyword(searchKeyword);
      setEnrollmentPage(0);
    }, 300);
    return () => {
      if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    };
  }, [searchKeyword]);

  // Changing the course filter resets to the first page, mirroring keyword behaviour.
  useEffect(() => {
    setEnrollmentPage(0);
  }, [selectedCourseId]);

  useEffect(() => {
    if (userId) getUsers(enrollmentPage, debouncedKeyword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, enrollmentPage, debouncedKeyword, selectedCourseId]);

  const sortedTableData = useMemo(
    () => sortEnrollmentsLocally(tableData || [], enrollmentSortBy, enrollmentSortDir),
    [tableData, enrollmentSortBy, enrollmentSortDir],
  );

  // Bulk delete selected enrollees. Endpoint + body for the API team:
  //   POST {API}/api/student/bulkDelete   body: { userId, studentIds: [<id>, ...] }
  // Typed delete confirmation shared by single + bulk delete.
  const [confirmState, setConfirmState] = useState({ open: false, count: 1, action: null });
  const askConfirm = (count, action) => setConfirmState({ open: true, count, action });
  const closeConfirm = () => setConfirmState({ open: false, count: 1, action: null });
  const runConfirm = async () => {
    const action = confirmState.action;
    closeConfirm();
    if (action) await action();
  };

  const handleBulkDelete = () => {
    if (!selectedRows.length) return;
    if (!guardAction('action.enrollment.bulkDelete')) return;
    askConfirm(selectedRows.length, doBulkDelete);
  };

  const doBulkDelete = async () => {
    if (!selectedRows.length) return;
    const studentIds = selectedRows.map((r) => r.id ?? r.studentId);
    try {
      await axios.post(`${apiUrl}/api/student/bulkDelete`, { userId, studentIds }, {
        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
          'Access-Control-Allow-Origin': '*',
          Authorization: getBasicAuthHeader(),
        },
      });
      setSelectedRows([]);
      invalidateEnrollmentListCache();
      getUsers(enrollmentPage, debouncedKeyword);
    } catch (error) {
      console.error('Bulk delete failed:', error);
      alert('Failed to delete the selected enrollees. Please try again.');
    }
  };

  // Cache-first load: instant when returning from detail/back or pagination previous.
  const getUsers = (page = 0, keyword = '') => {
    const listParams = {
      page,
      size: ENROLLMENT_PAGE_SIZE,
      keyword,
      courseId: selectedCourseId,
    };
    const cacheKey = buildEnrollmentListCacheKey(userId, listParams);
    const cached = getCache(cacheKey);
    if (cached) {
      applyEnrollmentListPage(cached);
      setLoading(false);
      return;
    }

    setLoading(true);
    const query = buildEnrollmentListQuery(listParams);
    fetch(`${apiUrl}/api/studentcourses/${userId}?${query}`, {
      headers: {
        'Content-Type': "application/json",
        "Accept": "*/*",
        "Access-Control-Allow-Origin": "*",
        'Authorization': getBasicAuthHeader(),
      },
    })
      .then(resp => resp.json())
      .then((resp) => {
        const parsed = parseEnrollmentListResponse(resp);
        applyEnrollmentListPage(parsed);
        setCache(cacheKey, parsed);
      })
      .catch(error => {
        console.error("Error fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleEnrollmentPageChange = (nextPage) => {
    const zeroBasedPage = Math.max(nextPage - 1, 0);
    setEnrollmentPage(zeroBasedPage);
  };

  const handleEnrollmentSortChange = (columnKey) => {
    const next = getNextEnrollmentSort(columnKey, enrollmentSortBy, enrollmentSortDir);
    setEnrollmentSortBy(next.sortBy);
    setEnrollmentSortDir(next.sortDir);
  };

  const handleFormSubmit = async () => {
    if (!guardAction('action.enrollment.save')) return;
    setLoading(true); // Set loading to true when submitting form
    //console.log(formData);
    if (formData.id) {
      const confirmed = await askConfirmation({
        title: 'Update enrollee?',
        message: 'Are you sure you want to update this row?',
        confirmLabel: 'Update',
      });
      if (!confirmed) {
        setLoading(false);
        return;
      }
      fetch(apiUrl + `/api/studentcourses/${userId}&${formData.studentId}`, {
        method: "PUT", body: JSON.stringify(formData), headers: { 'Content-Type': "application/json",
        "Accept": "*/*",
        "Access-Control-Allow-Origin": "*",
        'Authorization': getBasicAuthHeader(), }
      })
        .then(resp => resp.json())
        .then(resp => {
          handleClose();
          invalidateEnrollmentListCache();
          getUsers(enrollmentPage, debouncedKeyword);
        })
        .catch(error => {
          console.error("Error updating user:", error);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      fetch(apiUrl + `/api/studentcourses/${userId}`, {
        method: "POST",
        body: JSON.stringify(formData),
        headers: {
          "Content-Type": "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          "Authorization": getBasicAuthHeader(),
        },
      })
        .then(async (resp) => {
          const gateResult = await readResponseOrGate(resp);
          if (gateResult.gate) {
            openUpgradeGate(gateResult.body);
            return null;
          }
          const responseText = await gateResult.response.text();
          if (!gateResult.response.ok) {
            throw new Error(`${gateResult.response.status} - ${responseText}`);
          }
          return JSON.parse(responseText || "{}");
        })
        .then((resp) => {
          if (!resp) return;
          handleClose();
          if (userId) loadEntitlements(userId);
          invalidateEnrollmentListCache();
          getUsers(enrollmentPage, debouncedKeyword);
        })
        .catch((error) => {
          //console.error("Error adding new user:", error.message);
      
          if (error.message.includes("409")) {
            alert(`Participant has already enrolled in ${formData.courseName}`);
          } else if (error.message.includes("500")) {
            alert("A server error occurred. Please try again later.");
          } else {
            alert(`Failed to add student course: ${error.message}`);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleActiveStatus = async(data) => {
    if (!guardAction('action.enrollment.toggleStatus')) return;
    const isActive = data.activestatus === 1;
    const action = isActive ? 'inactive' : 'active';
    const actionLabel = isActive ? 'Set inactive' : 'Set active';
    const confirmed = await askConfirmation({
      title: `${actionLabel}?`,
      message: `Are you sure you want to set this enrollment ${action}?`,
      confirmLabel: isActive ? 'Inactive' : 'Active',
      variant: isActive ? 'danger' : 'default',
    });
    if (confirmed) {
      try {
        const activestatus = data.activestatus === 1 ? 0 : 1;
        const body = {
          studentId: data.studentId,
          activestatus: activestatus,
          courseId: data.courseId
        }
        const res = await axios.post(apiUrl+'/api/studentcourses/updateActiveStatus',body, {
          headers: {
            'Content-Type': "application/json",
            "Accept": "*/*",
            "Access-Control-Allow-Origin": "*",
            'Authorization': getBasicAuthHeader(),
          },
        });
      } catch (err) {
        //console.log(err.response ? err.response.data : 'Error');
      }
    }
    handleClose();
    invalidateEnrollmentListCache();
    getUsers(enrollmentPage, debouncedKeyword);
  }

  const handleClickOpen = () => {
    if (!guardAction('action.enrollment.create')) return;
    if (isMeterAtCap(entitlements, INSTRUCTOR_METERS.students)) {
      openUpgradeGate(meterGateBody(INSTRUCTOR_METERS.students, entitlements));
      return;
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setFormData(initialValue);
  };

  const onChange = (e) => {
    const { value, id } = e.target;
    // Functional update so multiple setFormData calls in one handler compose.
    // The course dropdown sets courseId and courseName back-to-back; spreading
    // stale `formData` would drop courseId (only the last update would win).
    setFormData((prev) => ({ ...prev, [id]: value }));
  };

  const handleUpdate = (rowData) => {
  if (!guardAction('action.enrollment.edit')) return;
  let dataToUpdate = rowData;

  

  setFormData({ ...dataToUpdate }); // Clone to avoid mutation
  handleClickOpen(); // Open the dialog
};


  const handleDelete = (rowData = null) => {
    if (!guardAction('action.enrollment.delete')) return;
    let rowsToDelete = [];
    if (rowData && rowData.id) {
      rowsToDelete = [rowData];
    } else {
      rowsToDelete = selectedRows;
    }
    if (rowsToDelete.length === 0) {
      alert("Please select row(s) to delete.");
      return;
    }
    askConfirm(rowsToDelete.length, () => doDelete(rowsToDelete));
  };

  const doDelete = (rowsToDelete) => {
  if (rowsToDelete.length > 0) {

    setLoading(true);

    const deleteRequests = rowsToDelete.map((row) =>
      fetch(`${apiUrl}/api/studentcourses/${userId}&${row.id}`, {
        method: "DELETE",
        headers: {
          'Content-Type': "application/json",
          "Accept": "*/*",
          "Access-Control-Allow-Origin": "*",
          'Authorization': getBasicAuthHeader(),
        },
      })
    );

    Promise.all(deleteRequests)
      .then((responses) => {
        const allSuccess = responses.every((resp) => resp.ok);
        if (!allSuccess) {
          console.error("Some deletions failed.");
        }
        invalidateEnrollmentListCache();
        getUsers(enrollmentPage, debouncedKeyword);
      })
      .catch((error) => {
        console.error("Network error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  } else {
    alert("Please select row(s) to delete.");
  }
};


  const handleOpenGradeCenter = () => {
    guardAction('action.enrollment.nav.gradeCenter', () => navigate('/Portal/Instructor/score'));
  };

  const handleViewScoresForRow = () => {
    guardAction('action.enrollment.viewScores', () => navigate('/Portal/Instructor/score'));
  };

  // Invite / import learners — UI options in place; APIs to be integrated later.
  const handleInviteLearners = () => {
    guardAction('action.enrollment.invite', () => {
      alert('Invite learners by email — coming soon (API integration pending).');
    });
  };

  const handleImportLearners = () => {
    guardAction('action.enrollment.import', () => {
      alert('Import learners from CSV / Excel — coming soon (API integration pending).');
    });
  };

  const handleAssignAssessmentForRow = () => {
    guardAction('action.enrollment.assignAssessment', () => {
      alert('Assign assessment — coming soon (API integration pending).');
    });
  };

  const getColumnDefs = (isMobile) => [
    {  headerName: "First Name", 
    field: "firstName", 
    checkboxSelection: !isMobile, 
    headerCheckboxSelection: !isMobile, // ✅ Add this line
    headerCheckboxSelectionFilteredOnly: true, // ✅ Selects only filtered rows (optional)
    hide: isMobile },
    { headerName: "Last Name", field: "lastName", hide: isMobile },
    { headerName: "Course", field: "courseName" },
    { headerName: "Email", field: "studentEmail" },
    { headerName: "Invite accepted", field: "emailVerified", hide: isMobile,
      cellRenderer: (params) => {
          return params.value === 1 ? 'Yes' : 'No';
      }, },
    { headerName: "Status", field: "activestatus",
      cellRenderer: (params) => {
          return params.value === 1 ? 'Active' : 'Inactive';
      }, },
    {
      headerName: "Actions", field: "id", width: 400,  
      cellRenderer: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            overflowX: "auto",
            overflowY: "hidden",
            maxWidth: "100%",
            alignItems: "center",
          }}
        >
          <MdEditNote
            data-tip="Update"
            style={{ margin: "10px", fontSize: "1.5em", color: "green", cursor: "pointer" }}
            onClick={() => handleUpdate(params.data)}
          />
          <MdDelete
            data-tip="Delete"
            style={{ margin: "10px", fontSize: "1.5em", color: "purple", cursor: "pointer" }}
            onClick={() => handleDelete(params.data)}
          />
          <Tooltip place="top" type="dark" effect="solid" />
        </div>
      )
    }
  ];

  useEffect(() => {
    const updateColumns = () => {
      const isMobile = window.innerWidth < 768;
      setColumnDefs(getColumnDefs(isMobile));
      setIsMobile(isMobile)
    };
  
    updateColumns(); // initial
    window.addEventListener("resize", updateColumns);
    return () => window.removeEventListener("resize", updateColumns);
  }, []);

  return (
    <PageShell>
      <PageHero>
        <div>
          <h2>Enrollments</h2>
          <p>Manage course access, status, and grade center workflows.</p>
        </div>
        <PageActionsMenu
          primaryLabel="Create learner"
          primaryIcon={<Plus size={16} strokeWidth={2.25} aria-hidden="true" />}
          onPrimaryClick={handleClickOpen}
          primaryLocked={isLocked('action.enrollment.create')}
          menuTitle="Enrollment tools"
          menuSubtitle="Add learners in bulk or jump to grade center."
        >
          {(closeActions) => (
            <>
              <ActionsSection label="Add learners">
                <ActionsMenuItem
                  label="Invite by email"
                  description="Send signup links to new participants"
                  icon={<Mail size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={handleInviteLearners}
                  {...lockProps('action.enrollment.invite')}
                />
                <ActionsMenuItem
                  label="Import from file"
                  description="Upload CSV or Excel roster"
                  icon={<Upload size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={handleImportLearners}
                  {...lockProps('action.enrollment.import')}
                />
              </ActionsSection>
              <ActionsSection label="Navigate">
                <ActionsMenuItem
                  label="Open grade center"
                  description="View scores and results"
                  icon={<BarChart2 size={15} strokeWidth={1.9} />}
                  closeActions={closeActions}
                  onClick={handleOpenGradeCenter}
                  {...lockProps('action.enrollment.nav.gradeCenter')}
                />
              </ActionsSection>
            </>
          )}
        </PageActionsMenu>
      </PageHero>

<TableWrap>
        <InstructorListStatsRow
          variant="enrollments"
          entitlements={entitlements}
          entitlementsLoading={entitlementsLoading}
          rowCount={rowCount}
          rows={sortedTableData}
          loading={loading}
          totalPages={enrollmentTotalPages}
        />
        <DataTable
          rows={sortedTableData}
          loading={loading}
          rowKey={(r, i) => r.id ?? i}
          selectable
          pageSize={ENROLLMENT_PAGE_SIZE}
          serverSide
          page={enrollmentPage + 1}
          totalPages={enrollmentTotalPages}
          totalElements={rowCount}
          onPageChange={handleEnrollmentPageChange}
          sortBy={enrollmentSortBy}
          sortDir={enrollmentSortDir}
          onSortChange={handleEnrollmentSortChange}
          searchValue={searchKeyword}
          onSearchChange={setSearchKeyword}
          onSelectionChange={setSelectedRows}
          bulkActions={(
            <Chip
              $soft
              style={{
                cursor: isLocked('action.enrollment.bulkDelete') ? 'not-allowed' : 'pointer',
                color: '#d6455d',
                opacity: isLocked('action.enrollment.bulkDelete') ? 0.55 : 1,
              }}
              onClick={handleBulkDelete}
            >
              Delete selected
            </Chip>
          )}
          search
          searchPlaceholder="Search participants, courses, emails…"
          filters={[
            { key: 'all', label: 'All', test: () => true },
            { key: 'active', label: 'Active', test: (r) => r.activestatus === 1 },
            { key: 'inactive', label: 'Inactive', test: (r) => r.activestatus !== 1 },
          ]}
          primary={{
            header: 'Participant',
            sortKey: ENROLLMENT_SORT_FIELDS.participant,
            label: (r) => `${r.firstName || ''} ${r.lastName || ''}`.trim() || r.studentEmail,
            sub: (r) => r.studentEmail,
          }}
          columns={[
            { header: 'Course', sortKey: ENROLLMENT_SORT_FIELDS.courseName, render: (r) => r.courseName, muted: true },
            { header: <LabelWithHelp help={HELP.inviteAccepted}>Invite accepted</LabelWithHelp>, sortKey: ENROLLMENT_SORT_FIELDS.inviteAccepted, render: (r) => (r.emailVerified === 1 ? 'Yes' : 'No'), muted: true },
            {
              header: <LabelWithHelp help={HELP.status}>Status</LabelWithHelp>,
              sortKey: ENROLLMENT_SORT_FIELDS.status,
              render: (r) => (
                <StatusBadge
                  $active={r.activestatus === 1}
                  style={{ cursor: 'pointer' }}
                  title={r.activestatus === 1 ? 'Set inactive' : 'Set active'}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActiveStatus(r);
                  }}
                >
                  {r.activestatus === 1 ? 'Active' : 'Inactive'}
                </StatusBadge>
              ),
            },
          ]}
          actionsColumnWidth={52}
          actions={(r) => (
            <EnrollmentRowActionsMenu
              row={r}
              onEdit={handleUpdate}
              onViewScores={handleViewScoresForRow}
              onAssignAssessment={handleAssignAssessmentForRow}
              onToggleStatus={handleActiveStatus}
              onDelete={handleDelete}
            />
          )}
          emptyText="No enrollments yet."
        />
      </TableWrap>
      <FormDialog
        open={open}
        handleClose={handleClose}
        data={formData}
        onChange={onChange}
        handleFormSubmit={handleFormSubmit}
        loading={loading}
      />
      <PackageDialog open={isPackageDialogOpen} handleClose={()=> setPackageDialogOpen(false)}/>
      <DeleteConfirmDialog
        open={confirmState.open}
        count={confirmState.count}
        onClose={closeConfirm}
        onConfirm={runConfirm}
      />
      <DemoLockedDialog open={lockedDialogOpen} onClose={() => setLockedDialogOpen(false)} />
    </PageShell>
  );
};

export default Grids;
